import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientContext';
import { useAuth0 } from '@auth0/auth0-react';
import Modal from 'react-modal';
import React, { useEffect, useState } from 'react';
import Spinner from './spinner/Spinner';
import Button from './button/Button';
import styles from './TermsGuard.module.scss';
import HabiTrackLegal from './policy/HabiTrackLegal';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(16, 20, 48, 0.50)',
    zIndex: 1000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)', 
    border: 'none',
    maxWidth: '500px',
    overflow: 'auto',
    maxHeight: '80%',
    minWidth: '80%',
  },
};

const TermsGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { logout } = useAuth0();
  const apiClient = useApiClient();
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const { mutate: accept, isPending } = useMutation({
    mutationFn: async () => {
      const res = await apiClient.api.acceptTermsAndPrivacyApiV1AuthAcceptTermsPost({
        accept_terms: true,
        accept_privacy: true,
      });
      return res?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      queryClient.invalidateQueries({ queryKey: ['userFlowStatusTerms'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const {
    data: userFlowStatus,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['userFlowStatusTerms'],
    queryFn: async () => {
      if (!apiClient?.api?.getUserFlowStatusApiV1AuthUserFlowStatusGet) {
        throw new Error('API client not ready');
      }
      const resp = await apiClient.api.getUserFlowStatusApiV1AuthUserFlowStatusGet();
      return resp?.data;
    },
  });

  useEffect(() => {
    if (userFlowStatus && userFlowStatus.has_accepted_terms === false) {
      setTermsModalOpen(true);
    } else {
      setTermsModalOpen(false);
    }
  }, [userFlowStatus]);

  if (isLoading)
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
        }}
      >
        <Spinner />
      </div>
    );
  if (error) return <div>Error: {(error as Error).message}</div>;

  const onAccept = () => {
    accept();
  };
  const onLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };
  if (userFlowStatus && userFlowStatus?.has_accepted_terms === false) {
    return (
      <Modal
        isOpen={termsModalOpen}
        contentLabel="Terms and Privacy Policy"
        style={customStyles}
        ariaHideApp={false}
      >
        <div>
          <HabiTrackLegal />
          <div className={styles.modalFooter}>
            <Button onClick={onLogout} disabled={isPending} variant="secondary">
              Decline & Logout
            </Button>
            <Button onClick={onAccept} disabled={isPending}>
              {isPending 
                ? 'Processing...' 
                : userFlowStatus?.requires_explicit_consent === false 
                  ? 'Continue' 
                  : 'Accept & Continue'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return <>{children}</>;
};

export default TermsGuard;
